<script setup>
import ActionButton from '@js/components/buttons/ActionButton.vue';
import DataTableLayout from '@js/components/tables/DataTableLayout.vue';
import BaseCell from '@js/components/tables/cells/BaseCell.vue';
import HeaderCell from '@js/components/tables/cells/HeaderCell.vue';
import LinkCell from '@js/components/tables/cells/LinkCell.vue';
import NumbersCell from '@js/components/tables/cells/NumbersCell.vue';
import PercentagesCell from '@js/components/tables/cells/PercentagesCell.vue';
import PricesCell from '@js/components/tables/cells/PricesCell.vue';
import TextCell from '@js/components/tables/cells/TextCell.vue';
import { useDataTable } from '@js/utils/dataTable';
import { hasRole } from '@js/utils/permission';
import { useRoute } from '@js/utils/route';
import { createColumnHelper } from '@tanstack/vue-table';
import { h } from 'vue';

const { type, apikom, days } = defineProps({
    type: {
        type: String,
        required: false,
    },
    apikom: {
        type: Boolean,
        required: false,
    },
    days: {
        type: Number,
        required: true,
    },
});

const { route } = useRoute();

const columnHelper = createColumnHelper();

const {
    loading,
    table,
    count,
    globalFilter,
    setGlobalFilter,
    availableFilters,
    filters,
    setFilters,
    globalData,
} = useDataTable({
    columns: [
        columnHelper.accessor(() => null, {
            id: 'name',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Nom',
                }),
            cell: (props) =>
                hasRole('super_admin')
                    ? h(LinkCell, {
                          text: props.row.original.name,
                          href: route(
                              'front.projectManagement.customer.updateCustomer',
                              {
                                  tenant: props.row.original.id,
                              },
                          ),
                      })
                    : props.row.original.url
                      ? h(LinkCell, {
                            text: props.row.original.name,
                            href: props.row.original.url,
                        })
                      : h(TextCell, {
                            text: props.row.original.name,
                        }),
        }),
        columnHelper.display({
            id: 'period',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Périodes',
                }),
            cell: () =>
                h(
                    'div',
                    {
                        class: 'flex h-full flex-col items-start justify-center p-4 whitespace-nowrap',
                    },
                    {
                        default: () => [
                            h('div', {
                                innerText:
                                    globalData.value.baseFrom +
                                    ' - ' +
                                    globalData.value.baseTo,
                                class: 'text-lg font-bold',
                            }),
                            h('div', {
                                innerText:
                                    globalData.value.comparison1From +
                                    ' - ' +
                                    globalData.value.comparison1To,
                            }),
                            h('div', {
                                innerText:
                                    globalData.value.comparison2From +
                                    ' - ' +
                                    globalData.value.comparison2To,
                            }),
                        ],
                    },
                ),
        }),
        columnHelper.accessor(() => null, {
            id: 'baseTurnover',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'CA',
                }),
            cell: (props) =>
                h(PricesCell, {
                    price1: props.row.original.baseTurnover,
                    price2: props.row.original.comparison1Turnover,
                    price3: props.row.original.comparison2Turnover,
                    withDecimals: false,
                    color:
                        props.row.original.comparison1Turnover === undefined
                            ? null
                            : props.row.original.baseTurnover ===
                                props.row.original.comparison1Turnover
                              ? 'orange'
                              : props.row.original.baseTurnover >
                                  props.row.original.comparison1Turnover
                                ? 'green'
                                : 'red',
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'baseSeaCost',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Coût SEA',
                }),
            cell: (props) =>
                props.row.original.googleAdsActive
                    ? h(PricesCell, {
                          price1: props.row.original.baseSeaCost,
                          price2: props.row.original.comparison1SeaCost,
                          price3: props.row.original.comparison2SeaCost,
                          withDecimals: false,
                      })
                    : null,
        }),
        columnHelper.accessor(() => null, {
            id: 'baseSeaCostsToOrderTurnoverRatio',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'SEA % CA HT',
                }),
            cell: (props) =>
                props.row.original.googleAdsActive
                    ? h(PercentagesCell, {
                          percentage1:
                              props.row.original
                                  .baseSeaCostsToOrderTurnoverRatio,
                          percentage2:
                              props.row.original
                                  .comparison1SeaCostsToOrderTurnoverRatio,
                          percentage3:
                              props.row.original
                                  .comparison2SeaCostsToOrderTurnoverRatio,
                          withDecimals: true,
                      })
                    : null,
        }),
        columnHelper.accessor(() => null, {
            id: 'baseSeaRoas',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'ROAS SEA',
                }),
            cell: (props) =>
                props.row.original.googleAdsActive
                    ? h(NumbersCell, {
                          number1: props.row.original.baseSeaRoas,
                          number2: props.row.original.comparison1SeaRoas,
                          number3: props.row.original.comparison2SeaRoas,
                          withDecimals: true,
                          color:
                              props.row.original.comparison1SeaRoas ===
                              undefined
                                  ? null
                                  : props.row.original.baseSeaRoas ===
                                      props.row.original.comparison1SeaRoas
                                    ? 'orange'
                                    : props.row.original.baseSeaRoas >
                                        props.row.original.comparison1SeaRoas
                                      ? 'green'
                                      : 'red',
                      })
                    : null,
        }),
        columnHelper.accessor(() => null, {
            id: 'baseSmaCost',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Coût SMA',
                }),
            cell: (props) =>
                props.row.original.facebookAdsActive
                    ? h(PricesCell, {
                          price1: props.row.original.baseSmaCost,
                          price2: props.row.original.comparison1SmaCost,
                          price3: props.row.original.comparison2SmaCost,
                          withDecimals: false,
                      })
                    : null,
        }),
        columnHelper.accessor(() => null, {
            id: 'baseSmaCostsToOrderTurnoverRatio',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'SMA % CA HT',
                }),
            cell: (props) =>
                props.row.original.facebookAdsActive
                    ? h(PercentagesCell, {
                          percentage1:
                              props.row.original
                                  .baseSmaCostsToOrderTurnoverRatio,
                          percentage2:
                              props.row.original
                                  .comparison1SmaCostsToOrderTurnoverRatio,
                          percentage3:
                              props.row.original
                                  .comparison2SmaCostsToOrderTurnoverRatio,
                          withDecimals: true,
                      })
                    : null,
        }),
        columnHelper.accessor(() => null, {
            id: 'baseSmaRoas',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'ROAS SMA',
                }),
            cell: (props) =>
                props.row.original.facebookAdsActive
                    ? h(NumbersCell, {
                          number1: props.row.original.baseSmaRoas,
                          number2: props.row.original.comparison1SmaRoas,
                          number3: props.row.original.comparison2SmaRoas,
                          withDecimals: true,
                          color:
                              props.row.original.comparison1SmaRoas ===
                              undefined
                                  ? null
                                  : props.row.original.baseSmaRoas ===
                                      props.row.original.comparison1SmaRoas
                                    ? 'orange'
                                    : props.row.original.baseSmaRoas >
                                        props.row.original.comparison1SmaRoas
                                      ? 'green'
                                      : 'red',
                      })
                    : null,
        }),
        columnHelper.accessor(() => null, {
            id: 'baseAdCostsToOrderTurnoverRatio',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'PUB / CA HT %',
                }),
            cell: (props) =>
                props.row.original.googleAdsActive ||
                props.row.original.facebookAdsActive
                    ? h(PercentagesCell, {
                          percentage1:
                              props.row.original
                                  .baseAdCostsToOrderTurnoverRatio,
                          suffix1: props.row.original.roasTargetPercentage
                              ? `(${props.row.original.roasTargetPercentage}%)`
                              : '',
                          percentage2:
                              props.row.original
                                  .comparison1AdCostsToOrderTurnoverRatio,
                          percentage3:
                              props.row.original
                                  .comparison2AdCostsToOrderTurnoverRatio,
                          withDecimals: true,
                          color:
                              props.row.original
                                  .comparison1AdCostsToOrderTurnoverRatio ===
                              undefined
                                  ? null
                                  : props.row.original.roasTargetPercentage
                                    ? props.row.original
                                          .baseAdCostsToOrderTurnoverRatio ===
                                      props.row.original.roasTargetPercentage
                                        ? 'orange'
                                        : Math.trunc(
                                                props.row.original
                                                    .baseAdCostsToOrderTurnoverRatio,
                                            ) >
                                            props.row.original
                                                .roasTargetPercentage
                                          ? 'red'
                                          : 'green'
                                    : 'orange',
                      })
                    : null,
        }),
        columnHelper.display({
            id: 'actions',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Actions',
                    width: 0,
                }),
            cell: (props) => {
                const original = props.row.original;

                const loginApikomUrl = `/${original.id}/login?token=${original.superAdminDatakeplerLoginToken}`;

                const loginCustomerUrl = original.superAdminDatakeplerLoginToken
                    ? `/${original.id}/login?token=${original.adminLoginToken}`
                    : original.loginToken
                      ? `/${original.id}/login?token=${original.loginToken}&asCustomer=1`
                      : `/${original.id}/login?token=${original.adminLoginToken}&asCustomer=1`;

                return h(
                    BaseCell,
                    { class: 'flex-col justify-center' },
                    {
                        default: () => [
                            original.superAdminDatakeplerLoginToken
                                ? h(
                                      ActionButton,
                                      {
                                          size: 'small',
                                          href: loginApikomUrl,
                                          target: '_blank',
                                          class: 'w-full whitespace-nowrap',
                                      },
                                      () => 'Connexion Apikom',
                                  )
                                : null,
                            h(
                                ActionButton,
                                {
                                    size: 'small',
                                    color: 'secondary',
                                    href: loginCustomerUrl,
                                    target: '_blank',
                                    class: 'w-full whitespace-nowrap',
                                    disabled:
                                        !original.loginToken &&
                                        !original.adminLoginToken,
                                },
                                () => 'Connexion client',
                            ),
                        ],
                    },
                );
            },
        }),
    ],
    url: route('front.postProjection'),
    data: {
        type,
        apikom,
        days,
    },
    columnVisibility: {
        baseTurnover:
            type === 'e-commerce' ||
            type === 'marketplace' ||
            type === 'shop' ||
            type === undefined,
        baseSeaCost:
            type === 'e-commerce' || type === 'showcase' || type === undefined,
        baseSeaCostsToOrderTurnoverRatio:
            type === 'e-commerce' || type === undefined,
        baseSeaRoas:
            type === 'e-commerce' || type === 'showcase' || type === undefined,
        baseSmaCost:
            type === 'e-commerce' || type === 'showcase' || type === undefined,
        baseSmaCostsToOrderTurnoverRatio:
            type === 'e-commerce' || type === undefined,
        baseSmaRoas:
            type === 'e-commerce' || type === 'showcase' || type === undefined,
        baseAdCostsToOrderTurnoverRatio:
            type === 'e-commerce' || type === undefined,
    },
});
</script>

<template>
    <DataTableLayout
        :table="table"
        :count="count"
        :global-filter="globalFilter"
        :set-global-filter="setGlobalFilter"
        :available-filters="availableFilters"
        :filters="filters"
        :set-filters="setFilters"
        :loading="loading"
        with-row-number
    />
</template>
